import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface UserPlan {
  id?: string;
  plan_id?: string;
  plan_name?: string;
  subscription_status?: string;
  trial_end_date?: string;
  created_at?: string;
  subscription_end_date?: string;
  subscription_plan?: string;
}

export const useUserPlan = (userId: string) => {
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasActivePlan, setHasActivePlan] = useState(false);
  const [isInTrialPeriod, setIsInTrialPeriod] = useState(false);
  const channelRef = useRef<any>(null);

  const calculatePlanStatus = (profileData: any) => {
    const now = new Date();
    let hasActivePlanValue = false;
    let isInTrialValue = false;

    // Verificar se tem plano ativo pela subscription_status E validade
    if (profileData?.subscription_status === 'active') {
      if (profileData.subscription_end_date) {
        const endDate = new Date(profileData.subscription_end_date);
        // Adiciona uma pequena margem de tolerância ou compara apenas data se necessário, 
        // mas comparando timestamp direto é mais seguro para expiração exata
        if (now <= endDate) {
          hasActivePlanValue = true;
        } else {
          // Expirou
          hasActivePlanValue = false;
        }
      } else {
        // Ativo e sem data de expiração (plano vitalício ou bug) -> considerar ativo
        hasActivePlanValue = true;
      }
    }

    // Verificar período de trial (7 dias a partir da criação da conta)
    // Esta regra vale para novos cadastros que não contrataram plano nenhum ainda.
    // Se o usuário tem um plano expirado (hasActivePlanValue = false), verificamos se ele ainda está na janela de 7 dias do cadastro.
    if (!hasActivePlanValue && profileData?.created_at) {
      const accountCreated = new Date(profileData.created_at);
      const trialEndDate = new Date(accountCreated);
      trialEndDate.setDate(trialEndDate.getDate() + 7);

      if (now <= trialEndDate) {
        isInTrialValue = true;
        hasActivePlanValue = true;
      }
    }

    return { hasActivePlanValue, isInTrialValue };
  };

  const checkUserPlan = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      // Verificar o perfil do usuário para informações adicionais
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Erro ao buscar perfil:', profileError);
        return;
      }

      const { hasActivePlanValue, isInTrialValue } = calculatePlanStatus(profileData);

      setUserPlan(profileData);
      setHasActivePlan(hasActivePlanValue);
      setIsInTrialPeriod(isInTrialValue);

    } catch (error) {
      console.error('Erro ao verificar plano do usuário:', error);
      // Resetar estados em caso de erro
      setUserPlan(null);
      setHasActivePlan(false);
      setIsInTrialPeriod(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      checkUserPlan();
      // Evitar múltiplas inscrições no mesmo canal
      if (channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch { }
        channelRef.current = null;
      }
      const channelName = `user-plan-${userId}-${Math.random().toString(36).slice(2)}`;
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
          (payload: any) => {
            const row = payload.new || payload.old || null;
            if (!row) return;

            const { hasActivePlanValue, isInTrialValue } = calculatePlanStatus(row);

            setUserPlan(row);
            setHasActivePlan(hasActivePlanValue);
            setIsInTrialPeriod(isInTrialValue);
          }
        )
        .subscribe();
      channelRef.current = channel;

      // Escutar eventos globais de atualização de plano (após pagamento)
      const handleRefresh = () => {
        console.log('🔄 useUserPlan - Recebeu evento de atualização');
        checkUserPlan();
      };

      window.addEventListener('userPlanRefresh', handleRefresh);
      window.addEventListener('profileRefresh', handleRefresh);
      window.addEventListener('planActivated', handleRefresh);

      return () => {
        try { supabase.removeChannel(channelRef.current || channel); } catch { }
        channelRef.current = null;
        window.removeEventListener('userPlanRefresh', handleRefresh);
        window.removeEventListener('profileRefresh', handleRefresh);
        window.removeEventListener('planActivated', handleRefresh);
      };
    }
  }, [userId]);

  return {
    userPlan,
    hasActivePlan,
    isInTrialPeriod,
    loading,
    refreshUserPlan: checkUserPlan
  };
};
